import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import { extractDataProps, extractAriaProps } from '@famly/mf_ts-utils_data-aria-props';
import { useModalsDialogsContext } from './modals-dialogs-context';
import { GenericDialog } from './generic-dialog';
/**
 * The hook to render a generic dialog.
 *
 * Allows easily showing a generic dialog with title, text, confirmation and cancel buttons.
 *
 * Allows specifying a `variant` prop which will change the confirm button's appearance.
 *
 * @see {@link file://./generic-dialog.tsx}
 * @returns an object containing functions to show and hide the dialog
 */
export function useGenericDialog() {
    const { showModalDialog, hideModalDialog } = useModalsDialogsContext();
    const id = React.useId();
    const descriptionId = `${id}-description`;
    const handleClose = React.useCallback(() => hideModalDialog(id), [hideModalDialog, id]);
    const show = React.useCallback(({ onClose, ...props }) => {
        var _a;
        if (props.autoConfirmLocalStorageKey && localStorage.getItem(props.autoConfirmLocalStorageKey) === 'true') {
            (_a = props.onConfirm) === null || _a === void 0 ? void 0 : _a.call(props);
        }
        else {
            const handleCloseRequest = () => {
                onClose === null || onClose === void 0 ? void 0 : onClose();
                handleClose();
            };
            showModalDialog({
                id,
                type: 'dialog',
                onClose: handleCloseRequest,
                element: _jsx(GenericDialog, { onClose: handleCloseRequest, descriptionId: descriptionId, ...props }),
                'aria-describedby': descriptionId,
                ...extractDataProps(props),
                ...extractAriaProps(props),
            });
        }
    }, [descriptionId, handleClose, id, showModalDialog]);
    return React.useMemo(() => ({
        showDialog: show,
        hideDialog: handleClose,
    }), [handleClose, show]);
}
